import { useState, useCallback, useRef, useEffect } from 'react';
import { apiFetch } from '../dppApi';

const SUGGESTIONS = [
    'Which products have no certificate?',
    'Where are my viewers located?',
    'Who owns my sold pieces?',
];

const SpeechRecognition =
    typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
const speechSupported = !!SpeechRecognition;

// Web Speech can't auto-detect the spoken language, so we dictate in the
// browser's language; the backend then detects/translates the transcript to
// English (same path as typed text) before sending it to Claude.
const DICTATION_LOCALE =
    (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en-US';

const MicIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
);

// Free-form assistant: asks the brand-scoped, read-only data agent a question
// and shows the plain answer, with the underlying query + rows on demand.
const BrandAssistant = ({ token }) => {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [showData, setShowData] = useState(false);
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);
    const inputRef = useRef(null);

    // Grow the textarea to fit its content so the full question stays visible
    // (typed or dictated), instead of clipping to a single line.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [question]);

    const submit = useCallback(async (text) => {
        const q = (text ?? question).trim();
        if (!q || loading) return;
        setLoading(true);
        setError(null);
        setResult(null);
        setShowData(false);
        try {
            const data = await apiFetch('/api/brand/assistant/ask', {
                method: 'POST', token, body: { question: q },
            });
            setResult(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [question, loading, token]);

    // Voice input via the browser's Web Speech API. When the user finishes
    // speaking, the final transcript fills the box and is submitted
    // automatically — no explicit "Ask" click needed. Click again to stop early.
    const toggleMic = useCallback(() => {
        if (!speechSupported) return;
        if (listening) { recognitionRef.current?.stop(); return; }
        const rec = new SpeechRecognition();
        rec.lang = DICTATION_LOCALE;
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            setQuestion(transcript);
            submit(transcript);
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => setListening(false);
        recognitionRef.current = rec;
        setListening(true);
        rec.start();
    }, [listening, submit]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
    };

    // Clear the answer and return the panel to its initial state (empty box +
    // suggestions), so the user can start a fresh question.
    const reset = useCallback(() => {
        recognitionRef.current?.stop();
        setQuestion('');
        setResult(null);
        setError(null);
        setShowData(false);
        inputRef.current?.focus();
    }, []);

    return (
        <div className="dpp-card insights-card assistant-card">
            <div className="insights-card-title">Ask Me Anything</div>

            <div className="assistant-input-row">
                <button
                    type="button"
                    className={`assistant-mic${listening ? ' is-listening' : ''}`}
                    onClick={toggleMic}
                    disabled={!speechSupported}
                    title={speechSupported ? (listening ? 'Stop' : 'Speak your question') : 'Voice input not supported'}
                    aria-label={listening ? 'Stop listening' : 'Speak your question'}
                >
                    {MicIcon}
                </button>
                <textarea
                    ref={inputRef}
                    className="dpp-input assistant-input"
                    rows={1}
                    placeholder=""
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                <button className="dpp-button" onClick={() => submit()} disabled={loading || !question.trim()}>
                    {loading ? '…' : 'Ask'}
                </button>
            </div>

            {!result && !loading && (
                <div className="assistant-suggestions">
                    {SUGGESTIONS.map(s => (
                        <button key={s} className="assistant-chip" onClick={() => { setQuestion(s); submit(s); }}>
                            {s}
                        </button>
                    ))}
                </div>
            )}

            {loading && <p className="assistant-status">Thinking…</p>}
            {error && <p className="dpp-error">{error}</p>}

            {result && (
                <div className="assistant-result">
                    <p className="assistant-answer">{result.answer}</p>
                    {result.columns?.length > 0 && (
                        <>
                            <button className="assistant-toggle" onClick={() => setShowData(v => !v)}>
                                {showData ? 'Hide data' : 'Show data'}
                            </button>
                            {showData && (
                                <div className="assistant-details">
                                    <div className="assistant-table-wrap">
                                        <table className="assistant-table">
                                            <thead>
                                                <tr>{result.columns.map(c => <th key={c}>{c}</th>)}</tr>
                                            </thead>
                                            <tbody>
                                                {result.rows.slice(0, 50).map((row, i) => (
                                                    <tr key={i}>
                                                        {row.map((cell, j) => (
                                                            <td key={j}>{cell === null ? '—' : String(cell)}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {result.truncated && <p className="assistant-status">Showing first {result.rows.length} rows.</p>}
                                </div>
                            )}
                        </>
                    )}
                    <div className="assistant-clear-row">
                        <button type="button" className="assistant-clear" onClick={reset}>
                            Clear
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandAssistant;
